import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Check, X, ChevronLeft, ChevronRight, CheckCircle2, Lock } from "lucide-react";
import api from "@/config/api";
import { useAuth } from "@/contexts/AuthContext";
import { GamifiedButton } from "@/components/GamifiedButton";
import { Loading } from "@/components/Feedback";
import { useSeo } from "@/hooks/useSeo";
import { cn } from "@/lib/utils";

const LEVELS = [
  { id: "debutant", label: "Débutant" },
  { id: "intermediaire", label: "Intermédiaire" },
  { id: "confirme", label: "Confirmé" },
];

export default function Reading() {
  useSeo("Apprendre à lire l'arabe", "Apprends à lire l'arabe pas à pas : alphabet, voyelles (harakât), soukoûn, chadda, tanwîn et lecture de mots. Niveaux débutant, intermédiaire et confirmé.");
  const { refresh } = useAuth();
  const qc = useQueryClient();
  const [level, setLevel] = useState("debutant");
  const [active, setActive] = useState(null);
  const [step, setStep] = useState(0);

  const { data: lessons, isLoading } = useQuery({ queryKey: ["reading-lessons"], queryFn: async () => (await api.get("/reading/lessons")).data });
  const { data: progress } = useQuery({ queryKey: ["reading-progress"], queryFn: async () => (await api.get("/reading/progress")).data });

  const complete = useMutation({
    mutationFn: async (lessonId) => (await api.post("/reading/complete", { lesson_id: lessonId })).data,
    onSuccess: async (d) => {
      if (!d.already) { toast.success("Leçon terminée ! +10 points ⭐"); await refresh(); }
      qc.invalidateQueries({ queryKey: ["reading-progress"] });
      setActive(null);
    },
    onError: () => toast.error("Erreur."),
  });

  if (isLoading) return <Loading full />;

  const completed = progress?.completed || [];
  const levelLessons = (lessons || []).filter((l) => l.level === level);
  const counts = progress?.counts?.[level] || { total: levelLessons.length, done: 0 };

  const openLesson = (lesson) => { setActive(lesson); setStep(0); };
  const item = active?.items?.[step];
  const isLast = active && step >= active.items.length - 1;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-black text-slate-800">Apprendre à lire</h1>
        <p className="text-slate-500">Maîtrise la lecture de l'arabe avant de mémoriser — lettres, voyelles et mots.</p>
      </div>

      {/* Level tabs */}
      <div className="inline-flex bg-slate-100 rounded-2xl p-1" data-testid="reading-levels">
        {LEVELS.map((lv) => (
          <button key={lv.id} data-testid={`reading-level-${lv.id}`} onClick={() => setLevel(lv.id)}
            className={cn("px-4 py-2.5 rounded-xl font-bold text-sm transition-colors",
              level === lv.id ? "bg-white text-primary shadow-sm" : "text-slate-500")}>
            {lv.label}
          </button>
        ))}
      </div>

      {/* Level progress */}
      <div className="bg-white p-4 rounded-3xl border border-slate-100 flex items-center gap-4">
        <div className="flex-1">
          <div className="flex items-center justify-between text-sm font-bold text-slate-500 mb-1">
            <span>Progression {LEVELS.find((l) => l.id === level).label}</span>
            <span>{counts.done}/{counts.total} leçons</span>
          </div>
          <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-primary to-secondary rounded-full transition-[width]"
              style={{ width: `${counts.total ? (counts.done / counts.total) * 100 : 0}%` }} />
          </div>
        </div>
      </div>

      {/* Lessons */}
      <div className="grid sm:grid-cols-2 gap-4">
        {levelLessons.map((lesson, i) => {
          const done = completed.includes(lesson.id);
          return (
            <motion.button key={lesson.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              onClick={() => openLesson(lesson)} data-testid={`reading-lesson-${lesson.order}`}
              className="flex items-center gap-4 bg-white p-5 rounded-3xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] text-left hover:-translate-y-1 transition-transform">
              <div className={cn("w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 font-arabic text-2xl",
                done ? "bg-emerald-100 text-emerald-600" : "bg-primary/10 text-primary")}>
                {done ? <CheckCircle2 className="w-7 h-7" /> : lesson.items[0]?.glyph?.slice(0, 2)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-extrabold text-slate-800">{lesson.title_fr}</div>
                <div className="text-sm text-slate-400">{lesson.subtitle_fr} · {lesson.items.length} éléments</div>
              </div>
              {done && <span className="text-xs font-bold bg-emerald-100 text-emerald-600 px-2.5 py-1 rounded-full">Terminée</span>}
            </motion.button>
          );
        })}
      </div>

      {/* Lesson viewer */}
      <AnimatePresence>
        {active && item && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-slate-900/40" onClick={() => setActive(null)}>
            <motion.div initial={{ y: 40 }} animate={{ y: 0 }} exit={{ y: 40 }} onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg bg-white rounded-3xl p-6 sm:p-8" data-testid="reading-viewer">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="font-extrabold text-slate-800">{active.title_fr}</div>
                  <div className="text-xs text-slate-400">Élément {step + 1} / {active.items.length}</div>
                </div>
                <button onClick={() => setActive(null)} data-testid="reading-close"><X className="w-5 h-5 text-slate-400" /></button>
              </div>

              <div className="flex flex-col items-center text-center py-6">
                <div className="font-arabic text-7xl sm:text-8xl text-slate-900 leading-none mb-4" data-testid="reading-glyph">{item.glyph}</div>
                <div className="text-xl font-black text-primary">{item.name_fr}</div>
                <div className="text-lg text-slate-500 font-semibold italic mt-1">« {item.translit} »</div>
                {item.example && <div className="text-sm text-slate-400 mt-3 bg-slate-50 px-4 py-2 rounded-2xl">{item.example}</div>}
              </div>

              <div className="flex items-center justify-between gap-3 mt-4">
                <button data-testid="reading-prev" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))}
                  className="w-12 h-12 rounded-2xl border-2 border-slate-200 flex items-center justify-center text-slate-500 disabled:opacity-40 hover:bg-slate-50">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                {isLast ? (
                  <GamifiedButton variant="secondary" className="flex-1" onClick={() => complete.mutate(active.id)} disabled={complete.isPending} data-testid="reading-finish">
                    <Check className="w-5 h-5" /> Terminer la leçon
                  </GamifiedButton>
                ) : (
                  <GamifiedButton className="flex-1" onClick={() => setStep((s) => s + 1)} data-testid="reading-next-item">
                    Suivant <ChevronRight className="w-5 h-5" />
                  </GamifiedButton>
                )}
                <button data-testid="reading-next" disabled={isLast} onClick={() => setStep((s) => Math.min(active.items.length - 1, s + 1))}
                  className="w-12 h-12 rounded-2xl border-2 border-slate-200 flex items-center justify-center text-slate-500 disabled:opacity-40 hover:bg-slate-50">
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
